/**
 * Lightweight non-blocking notification + confirm helpers.
 *
 * Replaces the legacy ``alert(...)`` / ``window.confirm(...)`` blocking
 * dialogs. ``notify.success / error / info / warning`` show a toast via
 * react-hot-toast. ``confirmAsync({...})`` opens a promise-based modal
 * that resolves to ``true`` / ``false`` — drop-in replacement for
 * ``window.confirm`` in async handlers.
 *
 * Usage:
 *
 *     import { notify, confirmAsync } from '../utils/notify';
 *
 *     notify.error('Errore di rete');
 *     if (!await confirmAsync({ message: 'Cancellare?' , danger: true })) return;
 *
 * Setup: ``<Toaster>`` and ``<ConfirmHost>`` must be mounted once at the
 * app root. See ``App.js``.
 */
import toast from 'react-hot-toast';

export const notify = {
  success: (message, options = {}) =>
    toast.success(message, { duration: 4000, ...options }),
  error: (message, options = {}) =>
    toast.error(message, { duration: 6000, ...options }),
  info: (message, options = {}) =>
    toast(message, { duration: 4000, ...options }),
  warning: (message, options = {}) =>
    toast(message, {
      icon: '⚠️',
      duration: 5000,
      style: { background: '#fffbeb', color: '#92400e', border: '1px solid #fde68a' },
      ...options,
    }),
  // Pass-through for advanced use (toast.promise, toast.loading, …).
  raw: toast,
};

// ─────────────────────────────────────────────────────────────────────
// Promise-based confirm modal
// ─────────────────────────────────────────────────────────────────────
// A single subscriber (the <ConfirmHost> component mounted at the app
// root) listens for ``open`` events and renders the modal. ``confirmAsync``
// dispatches an event with a unique id and returns a Promise that
// resolves when the host calls back with the result.

let confirmSubscriber = null;
let nextId = 0;

export function _registerConfirmSubscriber(fn) {
  confirmSubscriber = fn;
  return () => {
    if (confirmSubscriber === fn) confirmSubscriber = null;
  };
}

/**
 * Show a non-blocking confirm modal. Returns a Promise that resolves
 * to true (confirmed) or false (cancelled / dismissed).
 *
 * Options:
 *   - title           — optional heading (string)
 *   - message         — body text (string, required)
 *   - confirmLabel    — confirm button label (default: "Conferma")
 *   - cancelLabel     — cancel button label (default: "Annulla")
 *   - danger          — when true, confirm button is styled red
 */
export function confirmAsync({
  title = '',
  message = '',
  confirmLabel = 'Conferma',
  cancelLabel = 'Annulla',
  danger = false,
} = {}) {
  return new Promise((resolve) => {
    if (!confirmSubscriber) {
      // Fallback: no host mounted → degrade gracefully to window.confirm
      // so a missing setup never silently drops a destructive action.
      const ok = window.confirm(title ? `${title}\n\n${message}` : message);
      resolve(ok);
      return;
    }
    confirmSubscriber({
      id: ++nextId,
      title,
      message,
      confirmLabel,
      cancelLabel,
      danger,
      resolve,
    });
  });
}
